import { useDebounce } from '@/shared/lib/memo';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../Card';
import { Icon } from '../Icon';
import Input from '../Input';
import Loader from '../Loader';
import OptionSelector from '../OptionSelector/OptionSelector';
import { Typography } from '../Typography';
import styles from './OptionSelectorModal.module.scss';
interface SelectorModalProps<T extends { name: string }> {
	data: T[];
	isButtonDisabled: boolean;
	isFetching: boolean;
	onClick: () => void;
	placeholder: string;
	title: string;
	onBack?: () => void;
	onSelect?: (value: T) => void;
}

const OptionSelectorModal = <T extends { name: string }>({
	title,
	data,
	isFetching,
	placeholder,
	isButtonDisabled,
	onClick,
	onSelect,
	onBack,
}: SelectorModalProps<T>) => {
	const { t } = useTranslation('translation', { keyPrefix: 'permission' });
	const [selectedValue, setSelectedValue] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const debouncedQuery: string = useDebounce(searchQuery, 1000);

	const filteredData = data?.filter((item) => {
		if (!debouncedQuery || debouncedQuery.trim() === '') {
			return true;
		}

		return item.name.trim().toLowerCase().includes(debouncedQuery.trim().toLowerCase());
	});

	useEffect(() => {
		setSelectedValue(null);
		setSearchQuery('');
	}, [data]);

	const handleSelect = (item: T) => {
		setSelectedValue(item.name);
		onSelect?.(item);
	};

	return (
		<Card disabled={isButtonDisabled} textButton={t('done')} onClick={() => onClick()}>
			<header className={styles.header}>
				{onBack ? <Icon name='sprite/left-arrow' color='lightblue-40' onClick={onBack} /> : null}
				<Typography variant='title2' color='white' className={styles.headerHeading}>
					{title}
				</Typography>
				<Input
					variant='search'
					placeholder={placeholder}
					className={styles.headerInput}
					onChange={(value: string) => {
						setSearchQuery(value);
					}}
					onClear={() => {
						setSearchQuery('');
					}}
				/>
			</header>
			<div className='flex-1 overflow-y-auto pb-12 relative'>
				{isFetching ? (
					<div className='absolute inset-0 flex justify-center items-center pointer-events-none'>
						<Loader />
					</div>
				) : (
					<>
						{filteredData?.length > 0 ? (
							<ul>
								{filteredData.map((item: T, index: number) => {
									const value = item.name;

									return (
										<li key={`${value} ${index + 1}`}>
											<OptionSelector
												name={value}
												// eslint-disable-next-line max-len
												selected={value.trim().toLowerCase() === selectedValue?.trim().toLowerCase()}
												onClick={() => handleSelect(item)}
											/>
										</li>
									);
								})}
							</ul>
						) : (
							<div className='absolute inset-0 flex items-center justify-center'>
								<Typography variant='body3' color='white' className='text-center'>
									{t('emptyList')}
								</Typography>
							</div>
						)}
					</>
				)}
			</div>
		</Card>
	);
};

export default OptionSelectorModal;
